import { useCallback, useRef, useState } from 'react';
import type { IField } from '@models/IFormData';
import { DocumentDebugService, type IDirectWriteFailure } from '@services/DocumentDebugService';
import { isDirectWriteConfirmRequired } from './directWriteReason';

export interface IPendingDirectWrite {
    value: IField['value'];
    force: boolean;
}

type ConfirmedWrite = (value: IField['value'], force: boolean, reason?: string) => Promise<void>;

/** Заголовок, пояснение и доступность «Записать всё равно» для отказа прямой записи. */
export const describeDirectWriteFailure = (failure: IDirectWriteFailure) => {
    if (failure.status === 409 && failure.conflict === 'changed') {
        // force обходит только лок: перезаписать чужое сохранение нельзя, значения «было» уже перечитаны
        return { title: 'Документ изменился', detail: failure.message, canForce: false };
    }
    if (failure.status === 409) {
        const keeper = failure.lockInfo?.keeperUserName;
        const since = failure.lockInfo?.lockStartTime
            ? `, с ${new Date(failure.lockInfo.lockStartTime).toLocaleString()}`
            : '';
        return { title: 'Документ занят', detail: keeper ? `${keeper}${since}` : failure.message, canForce: true };
    }
    return { title: 'Отказ сервера', detail: failure.message, canForce: false };
};

/**
 * Запись жучком с подтверждением на PROD: «было → стало» с причиной, затем запись. Подтверждение остаётся
 * открытым до конца записи — его кнопка показывает ход.
 */
export function useConfirmedDirectWrite(write: ConfirmedWrite, onSuccess?: () => void) {
    const [confirmRequired] = useState(isDirectWriteConfirmRequired);
    const [pending, setPending] = useState<IPendingDirectWrite | null>(null);
    const [writing, setWriting] = useState(false);
    const [failure, setFailure] = useState<IDirectWriteFailure | null>(null);
    const writingRef = useRef(false);

    const perform = useCallback(
        async (value: IField['value'], force: boolean, reason?: string) => {
            if (writingRef.current) return;
            writingRef.current = true;
            setWriting(true);
            setFailure(null);
            try {
                await write(value, force, reason);
                setPending(null);
                onSuccess?.();
            } catch (error) {
                setPending(null);
                setFailure(DocumentDebugService.getFailure(error));
            } finally {
                writingRef.current = false;
                setWriting(false);
            }
        },
        [onSuccess, write],
    );

    /** Начать запись: на PROD — открыть подтверждение, иначе писать сразу. */
    const request = useCallback(
        (value: IField['value'], force = false) => {
            if (writingRef.current) return;
            setFailure(null);
            if (confirmRequired) {
                setPending({ value, force });
                return;
            }
            void perform(value, force);
        },
        [confirmRequired, perform],
    );

    const confirm = useCallback(
        (reason: string) => {
            if (pending) void perform(pending.value, pending.force, reason);
        },
        [pending, perform],
    );

    /** Снять подтверждение: правка значения, смена режима, отмена. Идущую запись не прерывает. */
    const reset = useCallback(() => {
        if (!writingRef.current) setPending(null);
    }, []);

    const fail = useCallback((message: string) => setFailure({ message }), []);
    const dismissFailure = useCallback(() => setFailure(null), []);

    return { confirmRequired, pending, writing, failure, request, confirm, reset, fail, dismissFailure };
}
